import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";
import { INDUSTRY_OPTIONS } from "@/constants/landing";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = {
  name: "",
  business: "",
  email: "",
  phone: "",
  industry: "",
  usage: "",
};

export default function AuditForm() {
  const [form, setForm] = useState(INITIAL);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/audit`, form);
      setSubmitted(true);
      toast.success("Got it — we'll call within 24 hours.");
    } catch (err) {
      toast.error("Something went wrong. Please try again or WhatsApp us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="audit" className="py-20 sm:py-28 relative" data-testid="audit-section">
      <div className="max-w-[1240px] mx-auto px-5 sm:px-8 grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-10 lg:gap-16 items-start">
        <div className="max-w-[520px]">
          <span className="yg-eyebrow">// Get started</span>
          <h2 className="yg-h2 text-[clamp(30px,4.6vw,52px)] mt-3 text-white">
            Free AI audit.
            <br />
            <span className="text-[var(--yg-violet-light)]">Custom pricing.</span>
          </h2>
          <p className="mt-5 text-[15px] leading-relaxed text-[var(--yg-text-mute)]">
            30-minute call. We map your ops, spot the AI leverage points, and quote for what you actually need — no packages, no fluff.
          </p>

          <ul className="mt-8 space-y-3">
            {["Ops audit + AI opportunity map", "Custom priced roadmap in 48 hours", "No obligation. No sales BS."].map((t) => (
              <li key={t} className="flex items-center gap-3 text-[14px] text-white/85">
                <span className="w-5 h-5 rounded-full bg-[var(--yg-violet)] flex items-center justify-center shrink-0">
                  <Check className="w-3 h-3 text-white" strokeWidth={3} />
                </span>
                {t}
              </li>
            ))}
          </ul>
        </div>

        <div className="yg-card p-5 sm:p-7 relative overflow-hidden" data-testid="audit-card">
          <div
            aria-hidden
            className="absolute -top-24 -right-16 w-[300px] h-[300px] rounded-full opacity-70 pointer-events-none"
            style={{ background: "radial-gradient(closest-side, rgba(108,58,237,0.28), transparent 70%)" }}
          />

          {submitted ? (
            <div className="relative min-h-[380px] flex flex-col items-center justify-center text-center py-10" data-testid="audit-success">
              <div className="w-14 h-14 rounded-full bg-[var(--yg-green)]/15 border border-[var(--yg-green)]/40 flex items-center justify-center mb-5">
                <Check className="w-7 h-7 text-[var(--yg-green)]" strokeWidth={3} />
              </div>
              <div className="yg-h2 text-[26px] text-white mb-2">You&apos;re in.</div>
              <p className="text-[14px] text-[var(--yg-text-mute)] max-w-[320px]">
                We&apos;ll call within 24 hours with your audit slot and a preview of the AI opportunities we spotted.
              </p>
              <button
                className="yg-btn yg-btn-ghost mt-6"
                onClick={() => { setForm(INITIAL); setSubmitted(false); }}
                data-testid="audit-reset"
              >
                Submit another
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="relative grid sm:grid-cols-2 gap-3.5" data-testid="audit-form">
              <Field label="Your name" testid="audit-name">
                <input className="yg-input" placeholder="Aarav Mehta" value={form.name} onChange={update("name")} data-testid="input-name" />
              </Field>
              <Field label="Business" testid="audit-business">
                <input className="yg-input" placeholder="Yugiq Retail Co." value={form.business} onChange={update("business")} data-testid="input-business" />
              </Field>
              <Field label="Email" testid="audit-email">
                <input type="email" className="yg-input" placeholder="you@company.com" value={form.email} onChange={update("email")} data-testid="input-email" />
              </Field>
              <Field label="Phone / WhatsApp" testid="audit-phone">
                <input className="yg-input" placeholder="+91 98…" value={form.phone} onChange={update("phone")} data-testid="input-phone" />
              </Field>
              <Field label="Industry" testid="audit-industry" full>
                <select className="yg-input appearance-none" value={form.industry} onChange={update("industry")} data-testid="input-industry">
                  <option value="">Select an industry</option>
                  {INDUSTRY_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </Field>
              <Field label="Current AI usage (optional)" testid="audit-usage" full>
                <textarea className="yg-input min-h-[92px] resize-none" placeholder="Tools you use, biggest ops bottleneck…" value={form.usage} onChange={update("usage")} data-testid="input-usage" />
              </Field>

              <div className="sm:col-span-2 flex items-center justify-between gap-3 pt-1">
                <div className="yg-mono text-[10px] tracking-[0.22em] uppercase text-[var(--yg-text-dim)]">
                  We reply within 24h
                </div>
                <button type="submit" disabled={loading} className="yg-btn yg-btn-primary" data-testid="audit-submit">
                  {loading ? "Sending…" : (<>Get Audit + Pricing <ArrowRight className="w-4 h-4" /></>)}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ label, children, full = false, testid }) {
  return (
    <label className={`flex flex-col gap-2 ${full ? "sm:col-span-2" : ""}`} data-testid={testid}>
      <span className="yg-mono text-[10px] tracking-[0.22em] uppercase text-[var(--yg-text-dim)]">{label}</span>
      {children}
    </label>
  );
}
